/* HC - IB Gift Engine (workstream A4) - 2026-07-20
 *
 * First-party replacement for EasyGift's storefront gift behavior.
 * Two layers, deliberately separated:
 *
 *   1. Pure evaluator  — IBGiftEngine.evaluate(cartLines, customerCtx, manifest, now)
 *                        computes desired reward state. No DOM, no network, no clock
 *                        reads of its own. Unit-tested directly.
 *   2. Reconciler      — subscribes to the theme cart event bus and converges the
 *                        cart toward the evaluator's desired state, acting only
 *                        AFTER the shopper's own mutation has settled.
 *
 * Hard invariants (see reports/ib-promotions-plan.md §3 — violating these is a bug):
 *   - Never patch the browser's network primitives (the EasyGift sin).
 *   - Never delay, wrap, or block the shopper's original cart mutation.
 *   - Zero network requests on the cart path: the manifest arrives before the
 *     shopper acts (window.IBGiftManifest from the loader) and all evaluation is
 *     local. The engine's only writes go through theme.cart's queued API.
 *   - At most ONE corrective cart mutation per reconcile pass, idempotent,
 *     converging within a bounded number of passes.
 *   - Reward lines are marked with the `_ib_reward` line property ("ruleId@version").
 *   - DEAL rules (2026-09-01) are paid add-ons: OFFERED via ibgift:state.deals,
 *     added only by acceptDeal(), marked `_ib_deal`, removed on dequalification.
 *     Other systems' line properties (EasyGift's gift marker, the shoppable-promotions
 *     marker) are never read or written.
 *   - Rewards are only inserted for rules whose manifest entry carries
 *     pricingVerified === true (an active pricing counterpart exists) — otherwise a
 *     "free" gift would be charged at full price.
 *   - Any error fails open: purchased items and the shopper's flow are unaffected.
 */
(function () {
  'use strict';

  var SOURCE = 'ib-gift-engine';
  var REWARD_PROP = '_ib_reward';
  /* HC - DEAL lines: a paid add-on the shopper accepted - 2026-09-01
   * "ruleId@version", like REWARD_PROP, but on a line the engine must NEVER add
   * on its own (acceptDeal is the only add path) and must remove when the cart
   * no longer qualifies — Discount Kit stops pricing the bundle then, and a
   * full-price bundle at checkout is the outcome this marker exists to prevent.
   * Kept apart from REWARD_PROP because the reconciler's entitlement model
   * would read a deal line as excess (no target wants it) and delete it. */
  var DEAL_PROP = '_ib_deal';
  var ENGINE_VERSION = '0.1.0';
  var MAX_PASSES_PER_TRIGGER = 3;
  var CHOICE_STORAGE_KEY = 'ib:gift:choices';
  var OPTOUT_STORAGE_KEY = 'ib:gift:optout';

  /* ------------------------------------------------------------------ *
   * Small utilities
   * ------------------------------------------------------------------ */

  function safeParseJSON(text, fallback) {
    try {
      var parsed = JSON.parse(text);
      return parsed === null || parsed === undefined ? fallback : parsed;
    } catch (err) {
      return fallback;
    }
  }

  function storageGet(key, fallback) {
    try {
      if (typeof sessionStorage === 'undefined') return fallback;
      var raw = sessionStorage.getItem(key);
      if (raw === null || raw === undefined) return fallback;
      return safeParseJSON(raw, fallback);
    } catch (err) {
      return fallback;
    }
  }

  function storageSet(key, value) {
    try {
      if (typeof sessionStorage === 'undefined') return;
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      /* private browsing / quota — opt-out and choices simply won't persist */
    }
  }

  function debugLog() {
    try {
      if (typeof console !== 'undefined' && console.debug) {
        console.debug.apply(console, ['[IBGiftEngine]'].concat([].slice.call(arguments)));
      }
    } catch (err) { /* noop */ }
  }

  function rewardToken(ruleId, version) {
    return String(ruleId) + '@' + String(version);
  }

  function parseRewardToken(value) {
    if (typeof value !== 'string') return null;
    var at = value.lastIndexOf('@');
    if (at <= 0) return null;
    return { ruleId: value.slice(0, at), version: value.slice(at + 1) };
  }

  /* ------------------------------------------------------------------ *
   * Manifest normalization
   *
   * Accepts the compact qualifier-manifest format (delta-encoded sorted id
   * arrays, `s: "d"`) or plain arrays, and an accompanying `rules` list.
   * ------------------------------------------------------------------ */

  function decodeIdArray(entry) {
    if (!entry || entry.length === 0) return [];
    var out = [entry[0]];
    for (var i = 1; i < entry.length; i++) {
      out.push(out[i - 1] + entry[i]);
    }
    return out;
  }

  function toIdSet(arr, isDelta) {
    var ids = isDelta ? decodeIdArray(arr || []) : (arr || []);
    var set = {};
    for (var i = 0; i < ids.length; i++) set[ids[i]] = true;
    return set;
  }

  /* HC - qualifier paths - 2026-10-26
   * `paths` is a list of alternative ways to earn a gift; each path is a list
   * of parts, { q: bucketId, min: units }, and needs every part. milk_shake's
   * Gloss offer: [[18 9 Minute tubes, 1 emulsion], [18 Creative tubes,
   * 1 emulsion]]. Returns [[{ bucket, min }]], or null when any path is empty
   * or any part names a missing bucket or a minimum that is not a whole
   * number of at least 1. */
  function normalizePaths(rawPaths, buckets) {
    if (!rawPaths || !rawPaths.length) return null;
    var out = [];
    for (var p = 0; p < rawPaths.length; p++) {
      var rawPath = rawPaths[p];
      if (!rawPath || !rawPath.length) return null;
      var path = [];
      for (var q = 0; q < rawPath.length; q++) {
        var part = rawPath[q] || {};
        var min = part.min;
        if (typeof min !== 'number' || !isFinite(min) || min < 1 || Math.floor(min) !== min) return null;
        if (!Object.prototype.hasOwnProperty.call(buckets, part.q)) return null;
        path.push({ bucket: part.q, min: min });
      }
      out.push(path);
    }
    return out;
  }

  function normalizeManifest(raw) {
    if (!raw || typeof raw !== 'object') return null;
    /* In ib-gift-manifest/v1 EVERY bucket array is delta-encoded ([first,
     * then differences]) and `s` is a SOURCE tag ('d' = discount-kit,
     * 'e' = easygift) — it is NOT an encoding flag. Misreading it left
     * every EasyGift bucket as raw deltas once (only the first ID matched);
     * the legacy `s === 'd'` reading survives only for fixture manifests. */
    var v1Schema = typeof raw.schema === 'string' && raw.schema.indexOf('ib-gift-manifest/') === 0;
    var buckets = {};
    var srcBuckets = raw.buckets || {};
    for (var id in srcBuckets) {
      if (!Object.prototype.hasOwnProperty.call(srcBuckets, id)) continue;
      var b = srcBuckets[id] || {};
      var isDelta = v1Schema || b.s === 'd';
      buckets[id] = {
        products: toIdSet(b.p, isDelta),
        variants: toIdSet(b.v, isDelta)
      };
    }

    var rules = [];
    /* Fixture/plain manifests carry engine rules directly; the v1+ schema
     * branch below replaces this with adapted gift entries. */
    var srcRules = raw.gifts && typeof raw.schema === 'string' && raw.schema.indexOf('ib-gift-manifest/') === 0
      ? []
      : (raw.rules || []);

    /* ib-gift-manifest/v1 (the A3 generator's shipped schema): executable gift
     * rules live in raw.gifts keyed by rule id — raw.rules there is DK
     * qualifier-membership metadata, not engine rules. Adapt each gift entry
     * to the engine's rule contract. */
    if (typeof raw.schema === 'string' && raw.schema.indexOf('ib-gift-manifest/') === 0 && raw.gifts) {
      srcRules = [];
      for (var gid in raw.gifts) {
        if (!Object.prototype.hasOwnProperty.call(raw.gifts, gid)) continue;
        var g = raw.gifts[gid] || {};
        var cond = g.cond || {};
        var metric = String(cond.metric || '').toUpperCase();
        /* COMBINATION triggers are not evaluable from the v1 manifest (per-
         * collection membership is merged away) — skip until manifest v2. */
        if (metric === 'COMBINATION') continue;
        if (!g.reward || !g.reward.options || !g.reward.options.length) continue;
        var isValue = metric === 'SUBTOTAL' || metric === 'VALUE';
        var modeRaw = String(g.mode || '').toUpperCase();
        /* 'deal' (2026-09-01): a paid add-on bundle. Offered, never inserted. */
        var mode = modeRaw === 'CHOICE' ? 'choice' : (modeRaw === 'DEAL' ? 'deal' : 'auto');
        /* HC - qualifier paths (2026-10-26): a gift earned by several buckets at
         * once. Such a gift lists every bucket in `q`, so an engine without
         * paths skips it (the length check below) instead of inserting it on
         * one bucket alone. A malformed path, a value metric or a DEAL skips
         * the gift too. */
        var paths = null;
        if (g.paths != null) {
          paths = normalizePaths(g.paths, buckets);
          if (!paths || isValue || mode === 'deal') continue;
        } else if (!g.q || g.q.length !== 1) {
          continue;
        }
        var options = [];
        for (var oi = 0; oi < g.reward.options.length; oi++) {
          var opt = g.reward.options[oi] || {};
          var normalized = {
            variantId: opt.v,
            productId: opt.p,
            quantity: mode === 'auto' ? (g.reward.qty || 1) : 1,
            oos: !!opt.oos
          };
          /* v2 display fields (title/thumbnail/handle) pass through for the
           * choice UI; absent on v1 manifests — UI falls back to placeholders. */
          if (opt.t) normalized.title = opt.t;
          if (opt.img) normalized.image = opt.img;
          if (opt.h) normalized.handle = opt.h;
          /* v2.1: retail value in dollars — the UI's "$N VALUE" tag. */
          if (typeof opt.val === 'number' && isFinite(opt.val) && opt.val > 0) normalized.value = opt.val;
          options.push(normalized);
        }
        var sched = g.sched || {};
        srcRules.push({
          id: gid,
          version: raw.meta && raw.meta.generatedAt ? raw.meta.generatedAt : 'v1',
          active: true,
          name: g.name,
          trigger: paths ? {
            /* bucket/min name the first part, for readers that want one. */
            bucket: paths[0][0].bucket,
            metric: 'quantity',
            min: paths[0][0].min,
            max: null,
            countRewardUnits: false,
            paths: paths,
            /* 'sum' only when the manifest says so; anything else is MAX. */
            pathMode: g.pathMode === 'sum' ? 'sum' : 'max'
          } : {
            bucket: g.q[0],
            metric: isValue ? 'value' : 'quantity',
            /* Manifest value thresholds are dollars; the engine compares
             * original_line_price cents. */
            min: isValue && cond.min != null ? Math.round(cond.min * 100) : cond.min,
            max: isValue && cond.max != null ? Math.round(cond.max * 100) : cond.max,
            /* EasyGift semantics: the qualifying count excludes gift lines. */
            countRewardUnits: false
          },
          /* per must live in the same unit as the evaluated amount: cents for
           * value rules (review F1: a dollars-denominated per made a $530 cart
           * compute floor(53025/497.5) = 106 reward units). */
          recurrence: {
            per: isValue && cond.min != null ? Math.round(cond.min * 100) : cond.min,
            /* HC - rec.cap: 0 means recurs without limit - 2026-09-01
             * The generator emits 0 for a recurring rule with no cap, N for a
             * capped one, 1 for earn-once, and older manifests carried null
             * for the first two. `|| 1` read both 0 and null as "once", so
             * every Discount Kit gift — all recurring, none capped — inserted
             * a single reward however many recurrences the cart had earned
             * and Discount Kit priced. recurrenceCount() already treats a
             * falsy cap as unbounded; only this coercion was in the way.
             * null / absent still means once, so an old manifest is no worse
             * than it was. */
            cap: (g.rec && typeof g.rec.cap === 'number') ? g.rec.cap : 1
          },
          reward: {
            mode: mode,
            options: options,
            /* HC - picks per earned gift (2026-10-26): a CHOICE gift whose
             * Discount Kit target is "6 Gloss shades" opens 6 slots each time
             * it is earned. AUTO gifts carry qty on each option instead. */
            slotsPer: mode === 'choice' && g.reward.qty > 1 ? Math.floor(g.reward.qty) : 1,
            sameVariant: !!g.reward.sameVariant,
            /* HC - first-in-stock AUTO gift (2026-10-26): options are an
             * ordered fallback; only the first in stock is inserted. */
            pick: mode === 'auto' && String(g.pick || '').toLowerCase() === 'first' ? 'first' : null
          },
          /* Deal rules sharing a tier group are TIERS of one offer (24 ->
           * Medium, 48 -> Large): only the highest tier the cart meets is
           * offered. The generator's `tier` key IS that group — it keys it by
           * container + prerequisite bucket NAME precisely because each tier
           * gets its own manifest bucket id, so the bucket id would give every
           * tier a group of one and silently disable supersession. A manifest
           * without `tier` leaves supersession off rather than faking it. */
          tierGroup: mode === 'deal' ? (g.tier || null) : null,
          deal: mode === 'deal' && g.deal ? {
            price: g.deal.price,
            regular: g.deal.regular,
            freeValue: g.deal.freeValue,
            paid: (g.deal.paid || []).slice(),
            free: (g.deal.free || []).slice()
          } : null,
          window: (sched.starts || sched.ends)
            ? { start: sched.starts || null, end: sched.ends || null }
            : null,
          /* pp 'priced' = verified DK pricing; 'zero-price' = $0 catalog
           * collateral. 'none' must never be inserted (unpriced reward). */
          pricingVerified: g.pp === 'priced' || g.pp === 'zero-price'
        });
      }
    }

    for (var i = 0; i < srcRules.length; i++) {
      var r = srcRules[i];
      if (!r || !r.id || !r.trigger || !r.reward) continue;
      if (r.active === false) continue;
      if (!buckets[r.trigger.bucket]) continue;
      if (!r.reward.options || !r.reward.options.length) continue;
      rules.push(r);
    }

    return {
      version: raw.version || (raw.meta && raw.meta.generatedAt) || 'unknown',
      buckets: buckets,
      rules: rules
    };
  }

  /* ------------------------------------------------------------------ *
   * Pure evaluator
   *
   * cartLines: /cart.js style items (variant_id, product_id, quantity,
   *            original_line_price in cents, properties).
   * customerCtx: { choices: { ruleId: [variantId, ...] }, now: msEpoch }
   * Returns { targets, choices, reasons } — never throws.
   * ------------------------------------------------------------------ */

  function lineIsEngineReward(line) {
    return !!(line && line.properties && line.properties[REWARD_PROP]);
  }

  function lineIsEngineDeal(line) {
    return !!(line && line.properties && line.properties[DEAL_PROP]);
  }

  /* HC - legacy-cart takeover (A8) - 2026-07-22
   * `_Gifted` is EasyGift's addedItemIdentifier (dependency audit: EG is its
   * only writer AND only reader; we never write it). After cutover, live carts
   * still carry EG-added gift lines. The engine ADOPTS them virtually: they
   * count toward fulfillment and are excluded from qualifier math exactly like
   * engine rewards, so the happy-path cutover pass mutates NOTHING. Excess or
   * dequalified legacy gifts converge like our own (trim/remove — matching
   * EG's own expired-gift cleanup). `_Gifted` lines whose variant matches no
   * known rule's reward option stay foreign-untouched, and Discount Kit's own
   * gift lines are that app's domain — never considered here. */
  var LEGACY_GIFT_PROP = '_Gifted';

  function lineIsLegacyGift(line) {
    return !!(line && line.properties &&
      Object.prototype.hasOwnProperty.call(line.properties, LEGACY_GIFT_PROP) &&
      !line.properties[REWARD_PROP]);
  }

  function ruleWindowOpen(rule, now) {
    var w = rule.window;
    if (!w) return true;
    if (w.start && now < w.start) return false;
    if (w.end && now >= w.end) return false;
    return true;
  }

  /* Whether a line can count toward a rule's trigger at all. */
  function countsTowardTrigger(rule, line) {
    if (!line) return false;
    /* Legacy EG gifts are excluded from qualifier math exactly like our own
     * rewards (EG itself excluded `_Gifted` lines from trigger calculation,
     * so this preserves pre-cutover counting semantics). */
    if ((lineIsEngineReward(line) || lineIsLegacyGift(line)) && !rule.trigger.countRewardUnits) return false;
    /* A deal bundle never counts toward any trigger — least of all its own
     * (the Beige bundle holds 24 CODE+ tubes; counted, it would bootstrap
     * the Medium tier it was bought under). */
    if (lineIsEngineDeal(line)) return false;
    return true;
  }

  function lineInBucket(bucket, line) {
    return !!(bucket.variants[line.variant_id] || bucket.products[line.product_id]);
  }

  function qualifyingAmount(rule, bucket, cartLines) {
    var metric = rule.trigger.metric === 'value' ? 'value' : 'quantity';
    var total = 0;
    for (var i = 0; i < cartLines.length; i++) {
      var line = cartLines[i];
      if (!countsTowardTrigger(rule, line) || !lineInBucket(bucket, line)) continue;
      if (metric === 'value') {
        total += typeof line.original_line_price === 'number'
          ? line.original_line_price
          : (line.line_price || 0);
      } else {
        total += line.quantity || 0;
      }
    }
    return total;
  }

  function recurrenceCount(rule, amount) {
    var min = rule.trigger.min || 0;
    if (amount < min) return 0;
    if (rule.trigger.max !== undefined && rule.trigger.max !== null && amount > rule.trigger.max) return 0;
    var rec = rule.recurrence;
    if (!rec || !rec.per) return 1;
    var k = Math.floor(amount / rec.per);
    if (k < 1) k = amount >= min ? 1 : 0;
    if (rec.cap) k = Math.min(k, rec.cap);
    return k;
  }

  /* How many times the cart earns a rule: { amount, k }.
   *
   * HC - qualifier paths - 2026-10-26
   * A path earns once per full set of its parts, so its count is the smallest
   * floor(units / min) across them: 36 tubes and one emulsion is one Gloss
   * set. Across paths the cart gets the LARGEST count, not the sum. The Gloss
   * paths are two Discount Kit rules in one container without Consume, and
   * both discount the same cheapest Gloss tubes, so a cart that meets both
   * gets the larger rule's free units, not both. Counting the sum would insert
   * units Discount Kit charges for. `amount` is the single-bucket amount; a
   * path rule reports 0 (nothing reads it there).
   *
   * pathMode 'sum' (2026-10-27) is the exception: see pathSumCount. */
  function earnedCount(rule, buckets, cartLines) {
    var paths = rule.trigger.paths;
    if (!paths) {
      var amount = qualifyingAmount(rule, buckets[rule.trigger.bucket], cartLines);
      return { amount: amount, k: recurrenceCount(rule, amount) };
    }
    if (rule.trigger.pathMode === 'sum') {
      return { amount: 0, k: pathSumCount(rule, buckets, cartLines) };
    }
    var best = 0;
    for (var p = 0; p < paths.length; p++) {
      var pathK = Infinity;
      for (var q = 0; q < paths[p].length; q++) {
        var part = paths[p][q];
        var have = qualifyingAmount(rule, buckets[part.bucket], cartLines);
        pathK = Math.min(pathK, Math.floor(have / part.min));
      }
      if (pathK > best) best = pathK;
    }
    var cap = rule.recurrence && rule.recurrence.cap;
    if (cap) best = Math.min(best, cap);
    return { amount: 0, k: best };
  }

  /* HC - qualifier paths, summed - 2026-10-27
   * Noah, 2026-10-27: 18 9 Minute + 18 Creative tubes + two emulsions earn two
   * Gloss sets. Discount Kit only frees two when the container has Consume
   * on: each rule then takes the units it discounts out of the pool, so the
   * second rule frees a second six shades and activator instead of the
   * first rule's. The builder writes pathMode 'sum' only for such a container.
   * Discount Kit never consumes qualifiers, so one emulsion could still count
   * for both rules (the accepted #269 gap); the engine doesn't insert that.
   * Each deal here takes its own units, the paths taking turns, and a unit
   * counted for one deal isn't counted again: 18 + 18 tubes and one emulsion
   * is one set, with two emulsions it is two. The cap applies to the total. */
  function pathSumCount(rule, buckets, cartLines) {
    var paths = rule.trigger.paths;
    var cap = (rule.recurrence && rule.recurrence.cap) || 0;
    var left = [];
    for (var i = 0; i < cartLines.length; i++) {
      left.push(countsTowardTrigger(rule, cartLines[i]) ? (cartLines[i].quantity || 0) : 0);
    }
    var deals = 0;
    var progressed = true;
    while (progressed) {
      progressed = false;
      for (var p = 0; p < paths.length; p++) {
        if (cap && deals >= cap) return deals;
        var taken = [];
        var complete = true;
        for (var q = 0; q < paths[p].length && complete; q++) {
          var bucket = buckets[paths[p][q].bucket];
          var need = paths[p][q].min;
          for (var l = 0; l < cartLines.length && need > 0; l++) {
            if (!left[l] || !lineInBucket(bucket, cartLines[l])) continue;
            var take = Math.min(left[l], need);
            left[l] -= take;
            need -= take;
            taken.push([l, take]);
          }
          if (need > 0) complete = false;
        }
        if (!complete) {
          for (var u = 0; u < taken.length; u++) left[taken[u][0]] += taken[u][1];
          continue;
        }
        deals += 1;
        progressed = true;
      }
    }
    return deals;
  }

  function evaluate(cartLines, customerCtx, manifest, now) {
    var result = { targets: [], choices: [], deals: [], reasons: [] };
    try {
      if (!manifest || !manifest.rules) return result;
      cartLines = cartLines || [];
      customerCtx = customerCtx || {};
      var choiceState = customerCtx.choices || {};
      var deadVariants = customerCtx.unavailableVariants || {};
      var effOptions = function (rule) {
        var out = [];
        for (var eo = 0; eo < rule.reward.options.length; eo++) {
          var op = rule.reward.options[eo];
          out.push(deadVariants[String(op.variantId)] && !op.oos
            ? { variantId: op.variantId, productId: op.productId, quantity: op.quantity,
                oos: true, title: op.title, image: op.image, handle: op.handle, value: op.value }
            : op);
        }
        return out;
      };
      now = typeof now === 'number' ? now : 0;

      /* HC - deal tiers (2026-09-01): among deal rules on one trigger bucket,
       * only the highest threshold the cart meets is OFFERED (24 -> Medium,
       * 48 -> Large; at 48 the Medium card disappears). Pre-pass so the loop
       * below can mark the lower tiers superseded. A lower tier the shopper
       * already accepted is still honoured by the reconciler — Discount Kit
       * keeps pricing it — it just is not offered again. */
      var dealTop = {};
      for (var dt = 0; dt < manifest.rules.length; dt++) {
        var dRule = manifest.rules[dt];
        if (dRule.reward.mode !== 'deal' || !dRule.tierGroup) continue;
        if (dRule.pricingVerified !== true || !ruleWindowOpen(dRule, now)) continue;
        var dBucket = manifest.buckets[dRule.trigger.bucket];
        if (!dBucket) continue;
        var dK = recurrenceCount(dRule, qualifyingAmount(dRule, dBucket, cartLines));
        if (dK === 0) continue;
        var top = dealTop[dRule.tierGroup];
        if (!top || (dRule.trigger.min || 0) > (top.trigger.min || 0)) dealTop[dRule.tierGroup] = dRule;
      }
      /* Deal lines already in the cart, per rule + variant, so the offer can
       * say "added" and cap what acceptDeal will add. dealHeld is the same
       * count per RULE, across every variant — what the shopper holds even
       * when no live option survives (see the !dealOpt branch below). */
      var dealInCart = {};
      var dealHeld = {};
      for (var dl = 0; dl < cartLines.length; dl++) {
        var dLine = cartLines[dl];
        if (!lineIsEngineDeal(dLine)) continue;
        var dTok = parseRewardToken(dLine.properties[DEAL_PROP]);
        if (!dTok) continue;
        var dKey = dTok.ruleId + '|' + dLine.variant_id;
        dealInCart[dKey] = (dealInCart[dKey] || 0) + (dLine.quantity || 0);
        dealHeld[dTok.ruleId] = (dealHeld[dTok.ruleId] || 0) + (dLine.quantity || 0);
      }

      for (var i = 0; i < manifest.rules.length; i++) {
        var rule = manifest.rules[i];
        var reason = function (code) {
          result.reasons.push({ ruleId: rule.id, code: code });
        };

        if (rule.pricingVerified !== true) { reason('pricing_unverified'); continue; }
        if (!ruleWindowOpen(rule, now)) { reason('not_in_window'); continue; }

        var earned = earnedCount(rule, manifest.buckets, cartLines);
        var amount = earned.amount;
        var k = earned.k;
        if (k === 0) {
          reason(amount > 0 && rule.trigger.max !== undefined && rule.trigger.max !== null && amount > rule.trigger.max
            ? 'above_maximum'
            : 'below_minimum');
          continue;
        }

        var options = effOptions(rule);
        if (rule.reward.mode === 'deal') {
          /* A DEAL is offered, never targeted: nothing here reaches
           * result.targets, so the reconciler can never insert it. The card
           * reads result.deals; acceptDeal() is the only add path. */
          var dealOpt = null;
          for (var dOpt = 0; dOpt < options.length; dOpt++) {
            if (!options[dOpt].oos) { dealOpt = options[dOpt]; break; }
          }
          if (!dealOpt) {
            /* HC - a sold-out second unit must not evict the first - 2026-09-01
             * A 422 on "Add another" marks the variant unavailable for the
             * session, so effOptions leaves no live option here. Dropping the
             * rule from result.deals would hand computeDiff allowed = 0 and it
             * would ZERO the bundle the shopper already accepted — a paid line
             * vanishing because a SECOND one was refused. Keep the entry while
             * units are held: slots earned, nothing remaining, flagged
             * unavailable so acceptDeal refuses it and the card reads "Added".
             * With nothing held there is nothing to protect: stay silent. */
            var heldUnits = dealHeld[rule.id] || 0;
            if (heldUnits > 0) {
              result.deals.push({
                ruleId: rule.id,
                version: rule.version,
                name: rule.name || '',
                slots: k,
                amount: amount,
                min: rule.trigger.min || 0,
                option: options[0] || null,
                deal: rule.deal,
                inCart: heldUnits,
                remaining: 0,
                superseded: !!(rule.tierGroup && dealTop[rule.tierGroup] && dealTop[rule.tierGroup].id !== rule.id),
                unavailable: true
              });
            }
            reason('reward_out_of_stock');
            continue;
          }
          var superseded = !!(rule.tierGroup && dealTop[rule.tierGroup] && dealTop[rule.tierGroup].id !== rule.id);
          var have = dealInCart[rule.id + '|' + dealOpt.variantId] || 0;
          result.deals.push({
            ruleId: rule.id,
            version: rule.version,
            name: rule.name || '',
            slots: k,
            amount: amount,
            min: rule.trigger.min || 0,
            option: dealOpt,
            deal: rule.deal,
            inCart: have,
            remaining: have >= k ? 0 : k - have,
            superseded: superseded
          });
          reason(superseded ? 'deal_superseded' : (have >= k ? 'deal_taken' : 'deal_available'));
          continue;
        }
        if (rule.reward.mode === 'choice') {
          /* HC - single-option promotion (decisions §8 addendum) - 2026-07-20
           * A "choice" with exactly one in-stock option is not a real choice:
           * auto-add it (the UI shows a toast instead of a picker). A sole
           * option that is out of stock is a quiet no-op for the rule. */
          /* Slots = picks per earned gift x times earned (6 Gloss shades a set). */
          var earnedSlots = k * (rule.reward.slotsPer || 1);
          /* HC - a refused top-up keeps its slots - 2026-10-27 (review R1-1)
           * A shade whose add was refused this session drops out of the
           * options, but the reconciler keeps the units of it the cart already
           * holds while the rule qualifies. Those units fill slots: without
           * counting them the picker re-offers all six and the cart ends with
           * more free shades than Discount Kit discounts. */
          var heldDead = 0;
          for (var hd = 0; hd < cartLines.length; hd++) {
            var hdLine = cartLines[hd];
            if (!lineIsEngineReward(hdLine) || !deadVariants[String(hdLine.variant_id)]) continue;
            var hdTok = parseRewardToken(hdLine.properties[REWARD_PROP]);
            if (!hdTok || hdTok.ruleId !== rule.id) continue;
            for (var hdo = 0; hdo < rule.reward.options.length; hdo++) {
              if (String(rule.reward.options[hdo].variantId) === String(hdLine.variant_id)) {
                heldDead += hdLine.quantity || 0;
                break;
              }
            }
          }
          heldDead = Math.min(heldDead, earnedSlots);
          var slots = earnedSlots - heldDead;
          var inStock = [];
          for (var io = 0; io < options.length; io++) {
            if (!options[io].oos) inStock.push(options[io]);
          }
          if (inStock.length === 1) {
            if (slots > 0) pushTarget(result.targets, rule, inStock[0], slots);
            reason('auto_single_option');
            continue;
          }
          if (inStock.length === 0) {
            /* Review F9: every option sold out = quiet no-op regardless of
             * option count — never a picker full of dead tiles. */
            reason('reward_out_of_stock');
            continue;
          }
          var selected = [];
          var chosen = choiceState[rule.id] || [];
          /* Review F7: stored picks fill slots and become targets ONLY while
           * their option is in stock — a sold-out pick neither inserts nor
           * blocks the slot (the picker resurfaces it as pending). */
          for (var c = 0; c < chosen.length && selected.length < slots; c++) {
            for (var o = 0; o < inStock.length; o++) {
              if (String(inStock[o].variantId) === String(chosen[c])) {
                selected.push(inStock[o]);
                break;
              }
            }
          }
          /* slots = what is left to pick; held = the refused shade's units
           * already filling the rest. */
          result.choices.push({
            ruleId: rule.id,
            version: rule.version,
            slots: slots,
            held: heldDead,
            options: options,
            selected: selected
          });
          for (var s = 0; s < selected.length; s++) {
            pushTarget(result.targets, rule, selected[s], 1);
          }
          reason(selected.length < slots ? 'choice_required' : 'qualified');
        } else if (rule.reward.pick === 'first') {
          /* HC - first-in-stock AUTO gift - 2026-10-26
           * One offer written as several Discount Kit rules in a MAXIMUM
           * container (Super Sync 12+: brush kit while it lasts, then the 500 mL
           * mask). Discount Kit prices ONE of them, so the cart must hold ONE
           * option, all k units of it — a kit plus a mask charges one.
           *   1. Stick with an option the cart already holds for this rule while
           *      the manifest still lists it in stock. If it went session-dead
           *      (a top-up was refused), add nothing: the reconciler keeps the
           *      held units (reward_out_of_stock still qualifies) and a second
           *      option would be charged.
           *   2. Otherwise the first option in listed order that is neither
           *      sold out in the manifest nor session-dead.
           * A held option the manifest now marks sold out is not stuck to:
           * the next option takes over and the dead line is trimmed. */
          var held = {};
          for (var hl = 0; hl < cartLines.length; hl++) {
            var hLine = cartLines[hl];
            if (!lineIsEngineReward(hLine)) continue;
            var hTok = parseRewardToken(hLine.properties[REWARD_PROP]);
            if (!hTok || hTok.ruleId !== rule.id) continue;
            held[String(hLine.variant_id)] = (held[String(hLine.variant_id)] || 0) + (hLine.quantity || 0);
          }
          var pickIdx = -1;
          for (var ph = 0; ph < rule.reward.options.length; ph++) {
            if (!rule.reward.options[ph].oos && held[String(rule.reward.options[ph].variantId)]) { pickIdx = ph; break; }
          }
          if (pickIdx === -1) {
            for (var pf = 0; pf < options.length; pf++) {
              if (!options[pf].oos) { pickIdx = pf; break; }
            }
          }
          if (pickIdx !== -1 && !options[pickIdx].oos) {
            pushTarget(result.targets, rule, options[pickIdx], k);
            reason('qualified');
          } else {
            reason('reward_out_of_stock');
          }
        } else {
          /* AUTO rules: never target a sold-out or session-dead reward — a
           * dead reward would otherwise retry-loop every pass. */
          var pushed = false;
          for (var a = 0; a < options.length; a++) {
            if (options[a].oos) continue;
            pushTarget(result.targets, rule, options[a], k);
            pushed = true;
          }
          reason(pushed ? 'qualified' : 'reward_out_of_stock');
        }
      }
    } catch (err) {
      debugLog('evaluate failed open', err);
      return { targets: [], choices: [], deals: [], reasons: [{ ruleId: null, code: 'evaluator_error' }] };
    }
    return result;
  }

  function pushTarget(targets, rule, option, multiplier) {
    var qty = (option.quantity || 1) * multiplier;
    for (var i = 0; i < targets.length; i++) {
      if (targets[i].ruleId === rule.id && String(targets[i].variantId) === String(option.variantId)) {
        targets[i].quantity += qty;
        return;
      }
    }
    targets.push({
      ruleId: rule.id,
      version: rule.version,
      variantId: option.variantId,
      quantity: qty
    });
  }

  /* HC - nudge progress projection (cart drawer v2, PR C) - 2026-08-20
   *
   * Read-only view over the SAME private helpers evaluate() uses
   * (ruleWindowOpen / qualifyingAmount / recurrenceCount), so the drawer's
   * nudge strip can say "4 more to unlock" without a second, drifting copy of
   * the threshold math. Purely additive: nothing above or below it changed, and
   * no caller of evaluate() sees any difference.
   *
   * Field mapping onto the evaluator's real internal shapes:
   *   metric        rule.trigger.metric — 'quantity' | 'value'. The manifest's
   *                 SUBTOTAL and VALUE metrics both normalize to 'value', whose
   *                 amounts are CENTS (normalizeManifest already converted the
   *                 manifest's dollars). Callers formatting money must use it.
   *   min / max     rule.trigger.min / .max, in the same unit as `amount`.
   *   per           rule.recurrence.per when the rule recurs, else trigger.min:
   *                 recurrenceCount treats a missing `per` as "earn once at
   *                 min", so min IS the step for those rules.
   *   nextThreshold the next rung the shopper can still reach — min while
   *                 nothing is earned, else (unitsEarned + 1) * per. null when
   *                 no rung is reachable: the recurrence cap is spent, or the
   *                 next rung would clear trigger.max.
   *   remaining     nextThreshold - amount; 0 when nextThreshold is null.
   *   pct           amount / nextThreshold clamped to 0..1; 1 when the rule is
   *                 earned and has no further rung; 0 when trigger.max has
   *                 dequalified the cart (nothing you add wins it back).
   *
   * Reward-line exclusion (_ib_reward / _Gifted), pricingVerified and window
   * gating are inherited from those helpers, never re-implemented.
   */
  function progress(cartLines, now) {
    var out = [];
    try {
      if (!state.manifest || !state.manifest.rules) return out;
      cartLines = cartLines || [];
      now = typeof now === 'number' ? now : 0;

      for (var i = 0; i < state.manifest.rules.length; i++) {
        var rule = state.manifest.rules[i];
        if (rule.pricingVerified !== true) continue;
        if (!ruleWindowOpen(rule, now)) continue;

        /* A path gift (2026-10-26) has no single amount to nudge toward:
         * "4 more tubes" would say nothing of the emulsion it also needs. */
        if (rule.trigger.paths) continue;

        var bucket = state.manifest.buckets[rule.trigger.bucket];
        if (!bucket) continue;

        var amount = qualifyingAmount(rule, bucket, cartLines);
        var unitsEarned = recurrenceCount(rule, amount);

        var min = rule.trigger.min || 0;
        var max = rule.trigger.max === undefined ? null : rule.trigger.max;
        var rec = rule.recurrence || {};
        /* A missing `per` makes recurrenceCount return 1 forever, whatever the
         * manifest claims about cap — mirror that instead of trusting rec.cap. */
        var per = rec.per || min;
        var cap = rec.per ? (rec.cap || 0) : 1;

        var next = null;
        if (unitsEarned === 0) {
          /* Above trigger.max is a dead end rather than a rung: no quantity the
           * shopper adds brings the cart back under the ceiling. */
          if (!(max !== null && amount > max)) next = min;
        } else if (!(cap && unitsEarned >= cap) && per > 0) {
          next = (unitsEarned + 1) * per;
          if (next < min) next = min;
          if (max !== null && next > max) next = null;
        }

        var pct;
        if (next && next > 0) {
          pct = amount / next;
          if (pct < 0) pct = 0;
          if (pct > 1) pct = 1;
        } else {
          pct = unitsEarned >= 1 ? 1 : 0;
        }

        out.push({
          ruleId: rule.id,
          name: rule.name || '',
          mode: rule.reward.mode,
          metric: rule.trigger.metric === 'value' ? 'value' : 'quantity',
          min: min,
          max: max,
          amount: amount,
          unitsEarned: unitsEarned,
          per: per,
          nextThreshold: next,
          remaining: next ? next - amount : 0,
          pct: pct,
          options: rule.reward.options.slice(),
          /* deal rules (2026-09-01): the nudge strip shows progress toward the
           * offer like any rule; tierGroup lets it hide a superseded tier. */
          tierGroup: rule.tierGroup || null,
          deal: rule.deal || null
        });
      }
    } catch (err) {
      debugLog('progress failed open', err);
      return [];
    }
    return out;
  }

  /* ------------------------------------------------------------------ *
   * Reconciler
   * ------------------------------------------------------------------ */

  var state = {
    initialized: false,
    active: false,
    manifest: null,
    busy: false,
    passCount: 0,
    unavailableVariants: {},
    newerCartWhileBusy: false,
    lastOwnedQty: null,
    lastCart: null
  };

  function themeCart() {
    return (typeof theme !== 'undefined' && theme && theme.cart) ||
      (typeof window !== 'undefined' && window.theme && window.theme.cart) || null;
  }

  function ownedRewardLines(cart, manifest) {
    var owned = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (!lineIsEngineReward(line)) continue;
      var token = parseRewardToken(line.properties[REWARD_PROP]);
      if (!token) continue;
      /* Engine-owned = the token's rule exists AND the line's variant is one
       * of that rule's reward options (review F15: a known ruleId on an
       * unrelated variant is foreign — never touched). Version is deliberately
       * lenient: every manifest regen changes it, and legacy gift lines must
       * stay managed across regens, not orphaned. */
      if (manifest && manifest.rules) {
        var rule = null;
        for (var r = 0; r < manifest.rules.length; r++) {
          if (manifest.rules[r].id === token.ruleId) { rule = manifest.rules[r]; break; }
        }
        /* Review R2-13: OUR token on a rule that no longer exists is engine
         * debris from a removed promotion — cleaned up (as `stale`), never
         * abandoned as a possibly-charged "gift". A KNOWN rule with a variant
         * outside its options stays foreign-untouched (F15): that shape is a
         * forgery, not our history. */
        if (!rule) {
          owned.push({ line: line, ruleId: token.ruleId, version: token.version, stale: true });
          continue;
        }
        var variantOk = false;
        for (var o = 0; o < rule.reward.options.length; o++) {
          if (String(rule.reward.options[o].variantId) === String(line.variant_id)) { variantOk = true; break; }
        }
        if (!variantOk) continue;
      }
      owned.push({ line: line, ruleId: token.ruleId, version: token.version });
    }
    return owned;
  }

  /* HC - engine-owned DEAL lines - 2026-09-01
   * Same validation as ownedRewardLines: our token, a known rule, the line's
   * variant among that rule's options. A token on a rule that has left the
   * manifest is stale debris and is cleaned up; a known rule with a foreign
   * variant is untouched. */
  function ownedDealLines(cart, manifest) {
    var owned = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (!lineIsEngineDeal(line)) continue;
      var token = parseRewardToken(line.properties[DEAL_PROP]);
      if (!token) continue;
      var rule = null;
      if (manifest && manifest.rules) {
        for (var r = 0; r < manifest.rules.length; r++) {
          if (manifest.rules[r].id === token.ruleId) { rule = manifest.rules[r]; break; }
        }
      }
      if (!rule) {
        owned.push({ line: line, ruleId: token.ruleId, version: token.version, stale: true });
        continue;
      }
      if (rule.reward.mode !== 'deal') continue;
      var variantOk = false;
      for (var o = 0; o < rule.reward.options.length; o++) {
        if (String(rule.reward.options[o].variantId) === String(line.variant_id)) { variantOk = true; break; }
      }
      if (!variantOk) continue;
      owned.push({ line: line, ruleId: token.ruleId, version: token.version, rule: rule });
    }
    return owned;
  }

  /* HC - units of one rule's deal bundle in a cart - 2026-09-01
   * Ownership-marked units only, optionally narrowed to one variant. Used to
   * settle an AMBIGUOUS accept: whether an add whose promise rejected actually
   * landed server-side. Reads the token directly rather than going through
   * ownedDealLines because it answers a counting question, not an ownership
   * one, and must stay truthful for a cart read taken mid-failure. */
  /* HC - is a deal line priced above what the card promised? - 2026-09-02
   * Reads the cart's own per-unit final price (cents, after line-level
   * discount allocations); falls back to final_line_price / quantity. Returns
   * false — never a guess — when the snapshot carries no price at all. */
  function dealLineOverpriced(line, rule) {
    if (!line || !rule || !rule.deal || !(rule.deal.price > 0)) return false;
    var qty = line.quantity || 0;
    var unit = null;
    if (typeof line.final_price === 'number') unit = line.final_price;
    else if (typeof line.final_line_price === 'number' && qty > 0) unit = Math.round(line.final_line_price / qty);
    if (unit === null || !isFinite(unit)) return false;
    var promised = Math.round(rule.deal.price * 100);
    return unit > promised + 1;
  }

  function dealUnitsInCart(items, ruleId, variantId) {
    var units = 0;
    items = items || [];
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (!lineIsEngineDeal(line)) continue;
      var token = parseRewardToken(line.properties[DEAL_PROP]);
      if (!token || String(token.ruleId) !== String(ruleId)) continue;
      if (variantId !== null && variantId !== undefined &&
          String(line.variant_id) !== String(variantId)) continue;
      units += line.quantity || 0;
    }
    return units;
  }

  /* Legacy EG gift lines attributed to the rule whose reward options contain
   * their variant. When a variant appears in several rules' options, prefer a
   * rule the current evaluation says qualifies (ruleQualifies is optional —
   * baseline callers pass null and get first-known attribution). Lines whose
   * variant matches no known rule are NOT returned: they are foreign debris
   * from retired promotions and stay untouched (fail-open). */
  function legacyRewardLines(cart, manifest, ruleQualifies) {
    var legacy = [];
    var items = (cart && cart.items) || [];
    if (!manifest || !manifest.rules) return legacy;
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (!lineIsLegacyGift(line)) continue;
      var chosen = null;
      for (var r = 0; r < manifest.rules.length; r++) {
        var rule = manifest.rules[r];
        for (var o = 0; o < rule.reward.options.length; o++) {
          if (String(rule.reward.options[o].variantId) === String(line.variant_id)) {
            if (!chosen) chosen = rule.id;
            if (ruleQualifies && ruleQualifies[rule.id]) { chosen = rule.id; r = manifest.rules.length; }
            break;
          }
        }
      }
      if (chosen) legacy.push({ line: line, ruleId: chosen });
    }
    return legacy;
  }

  function customerCtx() {
    return {
      choices: storageGet(CHOICE_STORAGE_KEY, {}),
      /* Variants whose add failed non-transiently this session: the evaluator
       * treats them as sold out, so a dead CHOICE option resurfaces the picker
       * with the alternatives instead of blocklisting the whole rule, and an
       * AUTO rule with a dead reward stops retry-looping. */
      unavailableVariants: state.unavailableVariants
    };
  }

  /* A stored pick lives exactly as long as the slot it fills (Noah 2026-07-21:
   * re-earning a choice offer must re-open the picker, never silently re-add
   * the previous pick). Rules that dequalify lose all stored picks; a shrunken
   * slot count drops the excess. Never prunes on an evaluator error. */
  function pruneChoices(desired) {
    for (var r = 0; r < desired.reasons.length; r++) {
      if (desired.reasons[r].code === 'evaluator_error') return;
    }
    var choices = storageGet(CHOICE_STORAGE_KEY, {});
    var byRule = {};
    for (var c = 0; c < desired.choices.length; c++) {
      var ch = desired.choices[c];
      var inStockSet = {};
      for (var o = 0; o < (ch.options || []).length; o++) {
        if (!ch.options[o].oos) inStockSet[String(ch.options[o].variantId)] = true;
      }
      byRule[ch.ruleId] = { slots: ch.slots, inStock: inStockSet };
    }
    var changed = false;
    for (var ruleId in choices) {
      if (!Object.prototype.hasOwnProperty.call(choices, ruleId)) continue;
      var entry = byRule[ruleId];
      if (!entry || entry.slots === 0) {
        delete choices[ruleId];
        changed = true;
        continue;
      }
      /* Review F8: drop picks whose option is sold out or gone BEFORE slicing,
       * so a replacement pick appended after an OOS one survives the trim. */
      var kept = [];
      for (var p = 0; p < choices[ruleId].length; p++) {
        if (entry.inStock[String(choices[ruleId][p])]) kept.push(choices[ruleId][p]);
      }
      if (kept.length > entry.slots) kept = kept.slice(0, entry.slots);
      if (kept.length !== choices[ruleId].length) {
        choices[ruleId] = kept;
        changed = true;
      }
    }
    if (changed) storageSet(CHOICE_STORAGE_KEY, choices);
  }

  /* A legacy EG gift that fills a CHOICE rule's option was picked by the
   * shopper pre-cutover (via EG's own picker). Seed the session pick state so
   * the modal presents that slot as already filled instead of re-asking.
   * Count-aware (a qty-2 line fills two slots), capped at the rule's slots,
   * in-stock options only (an OOS pick would be pruned immediately anyway).
   * Idempotent: appends only what stored picks don't already cover. */
  function seedLegacyChoicePicks(cart, desired, manifest) {
    if (!desired || !desired.choices || !desired.choices.length) return false;
    var chQualifies = {};
    for (var q = 0; q < desired.choices.length; q++) chQualifies[desired.choices[q].ruleId] = true;
    var legacy = legacyRewardLines(cart, manifest, chQualifies);
    if (!legacy.length) return false;
    var choices = storageGet(CHOICE_STORAGE_KEY, {});
    var changed = false;
    for (var c = 0; c < desired.choices.length; c++) {
      var ch = desired.choices[c];
      var optionOk = {};
      for (var o = 0; o < (ch.options || []).length; o++) {
        if (!ch.options[o].oos) optionOk[String(ch.options[o].variantId)] = true;
      }
      var legacyCount = {};
      for (var l = 0; l < legacy.length; l++) {
        if (legacy[l].ruleId !== ch.ruleId) continue;
        var v = String(legacy[l].line.variant_id);
        if (!optionOk[v]) continue;
        legacyCount[v] = (legacyCount[v] || 0) + (legacy[l].line.quantity || 0);
      }
      var stored = choices[ch.ruleId] || [];
      var storedCount = {};
      for (var s = 0; s < stored.length; s++) {
        var sv = String(stored[s]);
        storedCount[sv] = (storedCount[sv] || 0) + 1;
      }
      var appended = stored.slice();
      for (var lv in legacyCount) {
        if (!Object.prototype.hasOwnProperty.call(legacyCount, lv)) continue;
        var deficit = legacyCount[lv] - (storedCount[lv] || 0);
        while (deficit > 0 && appended.length < ch.slots) {
          appended.push(lv);
          deficit--;
        }
      }
      if (appended.length !== stored.length) {
        choices[ch.ruleId] = appended;
        changed = true;
      }
    }
    if (changed) storageSet(CHOICE_STORAGE_KEY, choices);
    return changed;
  }

  /* HC - picks live in the cart - 2026-10-27 (review R1-2)
   * Picks are kept in sessionStorage, which a second tab or a later session
   * doesn't have. The cart still holds the shades picked there, and with no
   * pick behind them computeDiff would remove all six (and the first tab
   * would then read the drop as an opt-out). So a qualifying CHOICE rule
   * adopts the in-stock units the engine already owns in the cart as picks,
   * the way seedLegacyChoicePicks adopts EG gifts: count-aware, capped at the
   * open slots, idempotent. Units past the slots stay unadopted and are
   * trimmed as before. A rule that no longer qualifies has no entry here, so
   * nothing comes back after a real dequalification; a shopper's removal
   * leaves fewer units than picks, so it adopts nothing either. Adopted EG
   * lines count with our own (seedLegacyChoicePicks has already stored picks
   * for them), so a shade held both ways is counted once in full. */
  function adoptOwnedChoicePicks(cart, desired, manifest) {
    if (!desired || !desired.choices || !desired.choices.length) return false;
    var chQualifies = {};
    for (var cqi = 0; cqi < desired.choices.length; cqi++) chQualifies[desired.choices[cqi].ruleId] = true;
    var owned = ownedRewardLines(cart, manifest).concat(legacyRewardLines(cart, manifest, chQualifies));
    if (!owned.length) return false;
    var choices = storageGet(CHOICE_STORAGE_KEY, {});
    var changed = false;
    for (var c = 0; c < desired.choices.length; c++) {
      var ch = desired.choices[c];
      var optionOk = {};
      for (var o = 0; o < (ch.options || []).length; o++) {
        if (!ch.options[o].oos) optionOk[String(ch.options[o].variantId)] = true;
      }
      var ownedCount = {};
      var order = [];
      for (var l = 0; l < owned.length; l++) {
        if (owned[l].stale || owned[l].ruleId !== ch.ruleId) continue;
        var v = String(owned[l].line.variant_id);
        if (!optionOk[v]) continue;
        if (!ownedCount[v]) order.push(v);
        ownedCount[v] = (ownedCount[v] || 0) + (owned[l].line.quantity || 0);
      }
      if (!order.length) continue;
      var stored = choices[ch.ruleId] || [];
      var storedCount = {};
      var live = 0;
      for (var s = 0; s < stored.length; s++) {
        var sv = String(stored[s]);
        if (!optionOk[sv]) continue;
        storedCount[sv] = (storedCount[sv] || 0) + 1;
        live += 1;
      }
      var room = Math.max(0, ch.slots - live);
      var appended = stored.slice();
      for (var ov = 0; ov < order.length && room > 0; ov++) {
        var deficit = ownedCount[order[ov]] - (storedCount[order[ov]] || 0);
        while (deficit > 0 && room > 0) {
          appended.push(order[ov]);
          deficit--;
          room--;
        }
      }
      if (appended.length !== stored.length) {
        choices[ch.ruleId] = appended;
        changed = true;
      }
    }
    if (changed) storageSet(CHOICE_STORAGE_KEY, choices);
    return changed;
  }

  function ownedQtyByKey(cart) {
    var owned = ownedRewardLines(cart, state.manifest);
    var map = {};
    for (var i = 0; i < owned.length; i++) {
      var key = owned[i].ruleId + '|' + owned[i].line.variant_id;
      map[key] = (map[key] || 0) + (owned[i].line.quantity || 0);
    }
    /* Adopted legacy gifts join the ownership baseline so a shopper deleting
     * one registers as an opt-out (otherwise the engine would re-add it). */
    var legacy = legacyRewardLines(cart, state.manifest, null);
    for (var l = 0; l < legacy.length; l++) {
      var lk = legacy[l].ruleId + '|' + legacy[l].line.variant_id;
      map[lk] = (map[lk] || 0) + (legacy[l].line.quantity || 0);
    }
    return map;
  }

  /* Every verified authoritative cart advances the ownership baseline — even
   * engine-sourced passes (review F4: skipping them meant a just-added gift was
   * never "previously owned", so an immediate shopper removal read as merely
   * missing and was re-added). */
  function updateOwnershipBaseline(cart) {
    state.lastOwnedQty = ownedQtyByKey(cart);
  }

  function detectOptOuts(cart, desired) {
    /* A shopper-initiated event where owned reward quantity DROPPED — fully or
     * partially (review F5) — while the engine still wants it = the shopper
     * removed or reduced the gift. Respect it for the rest of the session
     * (until qualification resets to zero). */
    var current = ownedQtyByKey(cart);

    var optout = storageGet(OPTOUT_STORAGE_KEY, {});
    var changed = false;

    if (state.lastOwnedQty) {
      var desiredQty = {};
      for (var t = 0; t < desired.targets.length; t++) {
        var dk = desired.targets[t].ruleId + '|' + desired.targets[t].variantId;
        desiredQty[dk] = desired.targets[t].quantity;
      }
      for (var key in state.lastOwnedQty) {
        if (!Object.prototype.hasOwnProperty.call(state.lastOwnedQty, key)) continue;
        var cur = current[key] || 0;
        if (cur >= state.lastOwnedQty[key]) continue;   /* no reduction */
        if ((desiredQty[key] || 0) > cur) {             /* we would top it back up */
          optout[key.slice(0, key.indexOf('|'))] = true;
          changed = true;
        }
      }
    }

    /* Reset opt-outs for rules that no longer qualify at all. A rule can
     * qualify with nothing to add: sold out, or (2026-10-27) every slot
     * filled by held units of a refused shade. Its reason still says it
     * qualifies, the same codes computeDiff reads, so the opt-out stands. */
    var qualifies = {};
    for (var q = 0; q < desired.targets.length; q++) qualifies[desired.targets[q].ruleId] = true;
    for (var cq = 0; cq < desired.choices.length; cq++) qualifies[desired.choices[cq].ruleId] = true;
    for (var rq = 0; rq < (desired.reasons || []).length; rq++) {
      var rEntry = desired.reasons[rq];
      if (rEntry && rEntry.ruleId && (rEntry.code === 'qualified' || rEntry.code === 'auto_single_option' ||
          rEntry.code === 'choice_required' || rEntry.code === 'reward_out_of_stock')) {
        qualifies[rEntry.ruleId] = true;
      }
    }
    for (var existing in optout) {
      if (Object.prototype.hasOwnProperty.call(optout, existing) && !qualifies[existing]) {
        delete optout[existing];
        changed = true;
      }
    }

    if (changed) storageSet(OPTOUT_STORAGE_KEY, optout);
    state.lastOwnedQty = current;
    return optout;
  }

  /* Review R1-1 (2026-10-27): how many units of a session-dead reward the
   * cart may keep while its rule qualifies. An AUTO option keeps its own
   * entitlement. A CHOICE or first-in-stock rule has one budget for the whole
   * rule: what it earns, less what its live targets already take. */
  function deadUnitAllowance(cart, desired, manifest) {
    var liveTargets = {};
    for (var t = 0; t < desired.targets.length; t++) {
      var tg = desired.targets[t];
      liveTargets[tg.ruleId] = (liveTargets[tg.ruleId] || 0) + (tg.quantity || 0);
    }
    var budget = {};
    return {
      take: function (ruleId, variantId, qty) {
        var rule = null;
        for (var r = 0; r < ((manifest && manifest.rules) || []).length; r++) {
          if (manifest.rules[r].id === ruleId) { rule = manifest.rules[r]; break; }
        }
        if (!rule) return 0;
        var k = earnedCount(rule, manifest.buckets, (cart && cart.items) || []).k;
        if (rule.reward.mode !== 'choice' && rule.reward.pick !== 'first') {
          for (var o = 0; o < rule.reward.options.length; o++) {
            var opt = rule.reward.options[o];
            if (String(opt.variantId) === String(variantId)) return Math.min(qty, (opt.quantity || 1) * k);
          }
          return 0;
        }
        if (!Object.prototype.hasOwnProperty.call(budget, ruleId)) {
          var per = rule.reward.mode === 'choice'
            ? (rule.reward.slotsPer || 1)
            : ((rule.reward.options[0] || {}).quantity || 1);
          budget[ruleId] = Math.max(0, per * k - (liveTargets[ruleId] || 0));
        }
        var granted = Math.min(qty, budget[ruleId]);
        budget[ruleId] -= granted;
        return granted;
      }
    };
  }

  function computeDiff(cart, desired, optout, manifest) {
    /* ownedRewardLines validates rule + reward-variant, so everything here is
     * genuinely engine-owned; forged/stale tokens never reach this list. */
    var owned = ownedRewardLines(cart, manifest);

    /* Which rules still qualify this pass — from the evaluator's own reasons.
     * reward_out_of_stock means qualified-but-unfulfillable; window/threshold/
     * pricing codes mean NOT qualified. */
    var ruleQualifies = {};
    for (var rq = 0; rq < (desired.reasons || []).length; rq++) {
      var reasonEntry = desired.reasons[rq];
      if (!reasonEntry || !reasonEntry.ruleId) continue;
      if (reasonEntry.code === 'qualified' || reasonEntry.code === 'auto_single_option' ||
          reasonEntry.code === 'choice_required' || reasonEntry.code === 'reward_out_of_stock') {
        ruleQualifies[reasonEntry.ruleId] = true;
      }
    }

    /* Removed-rule debris goes first: one update pass zeroes those lines
     * (review R2-13). They never enter the quantity model. */
    var live = [];
    var staleUpdates = {};
    var hasStale = false;
    for (var s = 0; s < owned.length; s++) {
      if (owned[s].stale) {
        staleUpdates[owned[s].line.key] = 0;
        hasStale = true;
      } else {
        live.push(owned[s]);
      }
    }
    owned = live;

    /* HC - DEAL lines: honour, cap, or remove — never add - 2026-09-01
     * A deal line the shopper accepted stays while its rule still qualifies
     * (offered, taken, or superseded by a higher tier — Discount Kit prices
     * every tier whose prerequisite is met). Below the threshold, or when the
     * window closed or the rule left the manifest, Discount Kit stops pricing
     * the bundle and it would reach checkout at its regular price, so the
     * line is removed and the UI is told why. Quantity above the earned
     * recurrence count is trimmed the same way a gift is. */
    /* An evaluator exception yields an EMPTY deals list — a failure to know,
     * not a dequalification. For a gift that costs a free item; for a DEAL it
     * would delete a bundle the shopper PAID for. Hold every owned deal line
     * as-is on that pass (review, 2026-09-02); pruneChoices already refuses
     * to act on evaluator_error the same way. */
    var evaluatorFailed = false;
    for (var ef = 0; ef < (desired.reasons || []).length; ef++) {
      if (desired.reasons[ef] && desired.reasons[ef].code === 'evaluator_error') { evaluatorFailed = true; break; }
    }
    var dealSlots = {};
    var dealOptions = {};
    for (var dd = 0; dd < (desired.deals || []).length; dd++) {
      var dEntry = desired.deals[dd];
      dealSlots[dEntry.ruleId] = dEntry.slots;
      dealOptions[dEntry.ruleId] = dEntry.option;
    }
    var ownedDeals = ownedDealLines(cart, manifest);
    var removedDeals = [];
    var dealQtyByRule = {};
    for (var od = 0; od < ownedDeals.length; od++) {
      var dOwned = ownedDeals[od];
      /* Written into staleUpdates: that map becomes `updates` below and rides
       * the same first-pass update call as removed-rule debris. */
      if (dOwned.stale) {
        staleUpdates[dOwned.line.key] = 0;
        hasStale = true;
        removedDeals.push({ ruleId: dOwned.ruleId, name: '', reason: 'rule_removed', lineKey: dOwned.line.key });
        continue;
      }
      if (evaluatorFailed) continue; /* keep the paid line; the next pass decides */
      var allowed = dealSlots[dOwned.ruleId] || 0;
      var already = dealQtyByRule[dOwned.ruleId] || 0;
      var lineQty = dOwned.line.quantity || 0;
      var keep = Math.max(0, Math.min(lineQty, allowed - already));
      /* HC - the advertised price must actually be on the line - 2026-09-02
       * Discount Kit is what turns $676.20 into $340.80; the engine only ever
       * asserted that a rule EXISTS (pricingVerified). If the app is off,
       * the rule changed, or the allocation failed, the bundle sits in the
       * cart at its regular price while the card says "Added". So compare the
       * authoritative unit price against rule.deal.price and remove a line
       * that costs more than promised (a cent of rounding tolerance; a LOWER
       * price — another discount stacking — is fine). A cart snapshot without
       * price fields (older payloads) skips the check rather than guessing. */
      if (keep > 0 && dealLineOverpriced(dOwned.line, dOwned.rule)) {
        keep = 0;
        staleUpdates[dOwned.line.key] = 0;
        hasStale = true;
        removedDeals.push({ ruleId: dOwned.ruleId, name: dOwned.rule.name || '', reason: 'unpriced', lineKey: dOwned.line.key, min: dOwned.rule.trigger.min || 0 });
        continue;
      }
      dealQtyByRule[dOwned.ruleId] = already + keep;
      if (keep < lineQty) {
        staleUpdates[dOwned.line.key] = keep;
        hasStale = true;
        /* Any trim is announced (review 2026-09-02): two bundles at 96 items
         * dropping to one at 48 is a paid unit leaving the cart, not a silent
         * quantity edit. keep === 0 keeps its dequalified/excess split. */
        removedDeals.push({ ruleId: dOwned.ruleId, name: dOwned.rule.name || '', reason: (keep === 0 && allowed === 0) ? 'dequalified' : 'excess', lineKey: dOwned.line.key, min: dOwned.rule.trigger.min || 0, trimmed: lineQty - keep, kept: keep });
      }
    }

    /* Adopted legacy EG gifts (A8 takeover) count toward fulfillment alongside
     * owned lines, so a cutover pass over an already-correct cart is a no-op
     * and never double-adds. They share the entitlement ceiling: excess (e.g.
     * a stale tab where EG's patch re-added) trims, dequalified known gifts
     * are removed — identical semantics to our own lines. */
    var legacy = legacyRewardLines(cart, manifest, ruleQualifies);

    /* Current gift quantity per rule+variant (owned + adopted legacy). */
    var currentQty = {};
    for (var i = 0; i < owned.length; i++) {
      var o = owned[i];
      var ckey = o.ruleId + '|' + o.line.variant_id;
      currentQty[ckey] = (currentQty[ckey] || 0) + (o.line.quantity || 0);
    }
    for (var li = 0; li < legacy.length; li++) {
      var lkey = legacy[li].ruleId + '|' + legacy[li].line.variant_id;
      currentQty[lkey] = (currentQty[lkey] || 0) + (legacy[li].line.quantity || 0);
    }

    /* Desired quantity per rule+variant — UNSUPPRESSED (reviews R2-6/R2-7:
     * opt-outs and dead variants gate ADDITIONS only; folding them in here
     * once turned the shopper's kept gift, or a valid line whose top-up
     * failed, into "excess" and deleted it). */
    var desiredQty = {};
    for (var d = 0; d < desired.targets.length; d++) {
      var target = desired.targets[d];
      desiredQty[target.ruleId + '|' + target.variantId] = {
        quantity: target.quantity,
        target: target
      };
    }

    /* Removals / down-adjustments: distribute the FULL excess across every
     * matching line in one update call (review F16: correcting only the first
     * duplicate line per pass could exhaust the pass budget with excess still
     * in the cart). */
    var updates = staleUpdates;
    var hasUpdates = hasStale;
    var excess = {};
    var deadAllowance = deadUnitAllowance(cart, desired, manifest);
    for (var ek in currentQty) {
      if (!Object.prototype.hasOwnProperty.call(currentQty, ek)) continue;
      /* The entitlement ceiling stands for every owned line (review R2-6: an
       * opt-out must not disable it — a later inflation above the rule's
       * entitlement still trims back). What opt-out and dead-variant status
       * suppress is ADDING, below. F5 stays honored because want reflects the
       * full entitlement: a shopper's reduction sits BELOW it, never above.
       * One carve-out (review R2-7): a session-dead variant vanishes from the
       * evaluator's targets entirely, so its want reads 0 — while its rule
       * still QUALIFIES (the evaluator says reward_out_of_stock, not
       * below_minimum), the shopper's existing unit is valid and kept. */
      var ekVariant = ek.slice(ek.indexOf('|') + 1);
      var ekRule = ek.slice(0, ek.indexOf('|'));
      if (state.unavailableVariants[ekVariant] && !desiredQty[ek] && ruleQualifies[ekRule]) {
        /* Review R1-1 (2026-10-27): kept, but only up to what the rule still
         * earns beyond its live targets. A refused top-up followed by new
         * picks, or by a smaller deal count, must not leave more free units
         * than Discount Kit discounts. */
        var keepDead = deadAllowance.take(ekRule, ekVariant, currentQty[ek]);
        if (currentQty[ek] > keepDead) excess[ek] = currentQty[ek] - keepDead;
        continue;
      }
      var wantQty = desiredQty[ek] ? desiredQty[ek].quantity : 0;
      if (currentQty[ek] > wantQty) excess[ek] = currentQty[ek] - wantQty;
    }
    /* Legacy lines absorb cuts FIRST: converging debris before marked lines
     * leaves surviving gifts engine-owned, so later passes get cleaner. */
    var cuttable = legacy.concat(owned);
    for (var r = 0; r < cuttable.length; r++) {
      var ow = cuttable[r];
      var dkey = ow.ruleId + '|' + ow.line.variant_id;
      if (!excess[dkey]) continue;
      var cut = Math.min(ow.line.quantity || 0, excess[dkey]);
      if (cut <= 0) continue;
      updates[ow.line.key] = (ow.line.quantity || 0) - cut;
      excess[dkey] -= cut;
      hasUpdates = true;
    }
    for (var xk in excess) {
      if (Object.prototype.hasOwnProperty.call(excess, xk) && excess[xk] > 0) {
        currentQty[xk] = (desiredQty[xk] ? desiredQty[xk].quantity : 0);
      }
    }

    /* Additions (one add call, possibly multi-item). Opt-outs and dead
     * variants suppress HERE — and only here. */
    var adds = [];
    for (var akey in desiredQty) {
      if (!Object.prototype.hasOwnProperty.call(desiredQty, akey)) continue;
      var wantEntry = desiredQty[akey];
      if (optout[wantEntry.target.ruleId]) continue;
      if (state.unavailableVariants[String(wantEntry.target.variantId)]) continue;
      var haveQty = currentQty[akey] || 0;
      if (wantEntry.quantity > haveQty) {
        var props = {};
        props[REWARD_PROP] = rewardToken(wantEntry.target.ruleId, wantEntry.target.version);
        adds.push({
          id: wantEntry.target.variantId,
          quantity: wantEntry.quantity - haveQty,
          properties: props,
          _ruleId: wantEntry.target.ruleId
        });
      }
    }

    return { updates: hasUpdates ? updates : null, adds: adds, removedDeals: removedDeals };
  }

  /* The UI's commit contract (review F2): after every reconciler mutation
   * settles, broadcast what actually happened so "Added to your order" can be
   * truthful. addedVariantIds = variants whose add call succeeded this pass. */
  function dispatchMutationResult(ok, addedVariantIds, failedRules) {
    try {
      document.dispatchEvent(new CustomEvent('ibgift:mutation-result', {
        detail: { ok: ok, addedVariantIds: addedVariantIds, failedRules: failedRules }
      }));
    } catch (err) { /* noop */ }
  }

  /* A challenge or network hiccup is transient: do NOT blocklist the rule for
   * the session — the next shopper event retries. Only concrete rejections
   * (OOS/422 product errors) mark a rule unavailable (review F6). */
  function isTransientAddError(err) {
    try {
      var cartApi = themeCart();
      if (cartApi && cartApi.isChallengeActive && cartApi.isChallengeActive()) return true;
    } catch (e) { /* noop */ }
    /* Review R2-9: throttles and server errors are conditions of the moment,
     * never proof a product is unavailable. */
    var status = err && (err.status || err.statusCode);
    if (typeof status === 'number' && (status === 429 || status >= 500)) return true;
    var msg = String((err && err.message) || err || '');
    return /network|failed to fetch|load failed|timed? ?out|429|too many requests|5\d\d|service unavailable|bad gateway/i.test(msg);
  }

  /* Review R2-1: post-failure convergence starts from an authoritative cart
   * read (theme-queued, deduped), never a stale snapshot — an ambiguous
   * failure may have landed server-side and a snapshot replay duplicates it.
   * Falls back to the in-flight partial result, else ends the pass. */
  function refreshedCartResult(fallbackResult) {
    try {
      var cartApi = themeCart();
      if (cartApi && typeof cartApi.getCart === 'function') {
        return cartApi.getCart().then(function (cart) {
          var cartState = cartApi.getCartState ? cartApi.getCartState(cart) : cart;
          if (cartState && Array.isArray(cartState.items)) return { cartState: cartState };
          return fallbackResult || null;
        }, function () { return fallbackResult || null; });
      }
    } catch (err) { /* fall through */ }
    return fallbackResult || null;
  }

  /* Batch add with per-item isolation (review F6): a batch rejection retries
   * each item individually so one sold-out reward cannot suppress the others,
   * and only the failing rule is blocklisted. */
  function addRewards(adds) {
    var cartApi = themeCart();
    var items = [];
    for (var i = 0; i < adds.length; i++) {
      items.push({
        id: adds[i].id,
        quantity: adds[i].quantity,
        properties: adds[i].properties,
        _ruleId: adds[i]._ruleId
      });
    }
    var send = function (item) {
      return cartApi.addSilently([{ id: item.id, quantity: item.quantity, properties: item.properties }], { source: SOURCE });
    };
    var batchPayload = [];
    for (var b = 0; b < items.length; b++) {
      batchPayload.push({ id: items[b].id, quantity: items[b].quantity, properties: items[b].properties });
    }
    return cartApi.addSilently(batchPayload, { source: SOURCE }).then(function (result) {
      var addedIds = [];
      for (var a = 0; a < items.length; a++) addedIds.push(String(items[a].id));
      dispatchMutationResult(true, addedIds, []);
      return result;
    }, function (batchErr) {
      if (items.length === 1) {
        var only = items[0];
        if (!isTransientAddError(batchErr)) state.unavailableVariants[String(only.id)] = true;
        dispatchMutationResult(false, [], [{ ruleId: only._ruleId, variantId: String(only.id), reason: String((batchErr && batchErr.message) || 'add_failed') }]);
        debugLog('reward add failed open', batchErr && batchErr.message);
        /* Review R2-1: NEVER converge from a stale snapshot after a failure —
         * an ambiguous network error may have landed the add server-side, and
         * replaying the add from a stale snapshot duplicates it. Refresh the
         * authoritative cart first; if that's unavailable, end the pass and
         * let the next shopper event resync. */
        return refreshedCartResult();
      }
      var added = [];
      var failed = [];
      var chain = Promise.resolve(null);
      items.forEach(function (item) {
        chain = chain.then(function (prev) {
          return send(item).then(function (result) {
            added.push(String(item.id));
            return result || prev;
          }, function (err) {
            if (!isTransientAddError(err)) state.unavailableVariants[String(item.id)] = true;
            failed.push({ ruleId: item._ruleId, variantId: String(item.id), reason: String((err && err.message) || 'add_failed') });
            return prev;
          });
        });
      });
      return chain.then(function (last) {
        dispatchMutationResult(failed.length === 0, added, failed);
        if (failed.length) return refreshedCartResult(last);
        return last;
      });
    });
  }

  function dispatchState(desired) {
    try {
      document.dispatchEvent(new CustomEvent('ibgift:state', {
        detail: {
          engineVersion: ENGINE_VERSION,
          manifestVersion: state.manifest ? state.manifest.version : null,
          targets: desired.targets,
          choices: desired.choices,
          deals: desired.deals || [],
          reasons: desired.reasons
        }
      }));
    } catch (err) { /* fail open */ }
  }

  function reconcile(cart, meta) {
    meta = meta || {};
    var cartApi = themeCart();
    if (!state.active || !cartApi || !cart || !Array.isArray(cart.items)) return null;
    if (state.busy) return null;
    if (cartApi.isChallengeActive && cartApi.isChallengeActive()) return null;

    var desired;
    try {
      desired = evaluate(cart.items, customerCtx(), state.manifest, Date.now());
      /* Legacy-cart takeover (A8): adopted EG choice gifts seed the session
       * picks; a seed changes the evaluation (slots fill, their targets
       * appear), so re-run once against the adopted state. */
      var seeded = seedLegacyChoicePicks(cart, desired, state.manifest);
      /* Review R1-2: shades picked in another tab or session, adopted the
       * same way, then evaluated once against the adopted state. */
      var adopted = adoptOwnedChoicePicks(cart, desired, state.manifest);
      if (seeded || adopted) {
        desired = evaluate(cart.items, customerCtx(), state.manifest, Date.now());
      }
      pruneChoices(desired);
      var optout;
      if (meta.fromEngine) {
        /* Engine-sourced verification: no opt-out detection (our own mutation
         * is not shopper intent) but the ownership baseline MUST advance so a
         * following shopper removal is seen as a transition (review F4). */
        updateOwnershipBaseline(cart);
        optout = storageGet(OPTOUT_STORAGE_KEY, {});
      } else {
        optout = detectOptOuts(cart, desired);
      }
      var diff = computeDiff(cart, desired, optout, state.manifest);
      dispatchState(desired);

      if (!diff.updates && diff.adds.length === 0) {
        state.passCount = 0;
        return null;
      }

      if (state.passCount >= MAX_PASSES_PER_TRIGGER) {
        debugLog('pass budget exhausted; deferring to next shopper event');
        state.passCount = 0;
        return null;
      }
      state.passCount += 1;
      state.busy = true;

      var mutation;
      if (diff.updates) {
        /* Stale rewards go first; additions happen on the follow-up pass. */
        mutation = cartApi.updateCartAndBroadcast(diff.updates, { source: SOURCE })
          .then(function (result) {
            dispatchMutationResult(true, [], []);
            /* A deal bundle that just lost its pricing was in those updates;
             * say so, or the shopper sees a paid line vanish without a word. */
            dispatchDealRemovals(diff.removedDeals);
            return result;
          }, function (err) {
            dispatchMutationResult(false, [], []);
            debugLog('reward update failed open', err && err.message);
            return null;
          });
      } else {
        mutation = addRewards(diff.adds);
      }

      return mutation.then(function (result) {
        state.busy = false;
        /* Review R2-5: a shopper event that arrived mid-mutation is newer
         * truth than this mutation's snapshot — drain it instead. */
        if (state.newerCartWhileBusy) {
          state.newerCartWhileBusy = false;
          state.passCount = 0;
          return reconcile(state.lastCart, { fromEngine: false });
        }
        var cartState = result && cartApi.getCartState ? cartApi.getCartState(result) : null;
        if (cartState && Array.isArray(cartState.items)) {
          state.lastCart = cartState;
          return reconcile(cartState, { fromEngine: true });
        }
        return null;
      }, function (err) {
        state.busy = false;
        debugLog('reconcile mutation failed open', err && err.message);
        if (state.newerCartWhileBusy) {
          state.newerCartWhileBusy = false;
          state.passCount = 0;
          return reconcile(state.lastCart, { fromEngine: false });
        }
        return null;
      });
    } catch (err) {
      state.busy = false;
      debugLog('reconcile failed open', err);
      return null;
    }
  }

  function onCartUpdated(event) {
    try {
      var detail = (event && event.detail) || {};
      if (detail.source === SOURCE) return;
      if (detail.optimistic || detail.countOnly || detail.lightweight) return;
      var cart = detail.cart || detail.cartState;
      if (!cart || !Array.isArray(cart.items)) return;
      if (detail.cartSwap) {
        /* A wholesale cart rewrite (saved-cart load, reorder replace, restore) is
         * not the shopper removing gifts. Fresh cart, fresh intent: forget opt-outs
         * and re-baseline so this pass re-derives rewards from scratch. */
        storageSet(OPTOUT_STORAGE_KEY, {});
        state.lastOwnedQty = null;
      }
      state.lastCart = cart;
      state.passCount = 0;
      /* Review R2-5: while a mutation is in flight this pass no-ops on the
       * busy guard — flag it so the continuation drains the NEWER cart
       * instead of clobbering it with its own older result. */
      if (state.busy) state.newerCartWhileBusy = true;
      reconcile(cart, { fromEngine: false });
    } catch (err) {
      debugLog('cart:updated handler failed open', err);
    }
  }

  /* ------------------------------------------------------------------ *
   * Choice API (consumed by the A5 choice UI)
   * ------------------------------------------------------------------ */

  function storeChoices(ruleId, variantIds) {
    var choices = storageGet(CHOICE_STORAGE_KEY, {});
    var list = choices[ruleId] || [];
    /* One entry per earned slot, duplicates allowed — two slots may hold the
     * same variant. Excess entries are pruned against live slot counts. */
    for (var i = 0; i < variantIds.length; i++) list.push(String(variantIds[i]));
    choices[ruleId] = list;
    storageSet(CHOICE_STORAGE_KEY, choices);
    document.dispatchEvent(new CustomEvent('ibgift:choices-updated', { detail: { ruleId: ruleId } }));
  }

  function selectChoice(ruleId, variantId) {
    try {
      storeChoices(ruleId, [variantId]);
      if (state.lastCart) reconcile(state.lastCart, { fromEngine: false });
    } catch (err) {
      debugLog('selectChoice failed open', err);
    }
  }

  /* Commit a whole choice at once: all picks stored, then a SINGLE reconcile —
   * so a multi-pick commit produces one add batch and therefore one
   * ibgift:mutation-result carrying the full ack set (the UI's commit machine
   * confirms 'added' only when every committed variant is acknowledged). */
  function selectChoices(ruleId, variantIds) {
    try {
      var ids = [];
      for (var i = 0; i < (variantIds || []).length; i++) {
        if (variantIds[i] != null) ids.push(variantIds[i]);
      }
      if (!ids.length) return;
      storeChoices(ruleId, ids);
      if (state.lastCart) reconcile(state.lastCart, { fromEngine: false });
    } catch (err) {
      debugLog('selectChoices failed open', err);
    }
  }

  function clearChoices(ruleId) {
    try {
      var choices = storageGet(CHOICE_STORAGE_KEY, {});
      delete choices[ruleId];
      storageSet(CHOICE_STORAGE_KEY, choices);
      document.dispatchEvent(new CustomEvent('ibgift:choices-updated', { detail: { ruleId: ruleId } }));
      if (state.lastCart) reconcile(state.lastCart, { fromEngine: false });
    } catch (err) {
      debugLog('clearChoices failed open', err);
    }
  }

  /* ------------------------------------------------------------------ *
   * HC - Deal API (consumed by the choice UI's deal card) - 2026-09-01
   *
   * The ONLY path that puts a deal bundle in the cart. The reconciler never
   * adds one (a deal is not a target); it only removes or trims. acceptDeal
   * re-validates against the last evaluation — the rule must be offered,
   * unsuperseded, in stock, with a slot left — then adds exactly one unit
   * through the theme's queued cart API with the DEAL_PROP token, under the
   * same busy / challenge guards the reconciler observes. Resolves to
   * { ok, variantId, reason } and dispatches ibgift:mutation-result either
   * way so the card can confirm or offer Retry. Never throws.
   * ------------------------------------------------------------------ */
  function acceptDeal(ruleId) {
    var refuse = function (reason) {
      dispatchMutationResult(false, [], [{ ruleId: String(ruleId), variantId: null, reason: reason }]);
      return Promise.resolve({ ok: false, variantId: null, reason: reason });
    };
    try {
      if (!state.active || !state.manifest) return refuse('engine_inactive');
      var cartApi = themeCart();
      if (!cartApi || typeof cartApi.addSilently !== 'function') return refuse('cart_unavailable');
      if (cartApi.isChallengeActive && cartApi.isChallengeActive()) return refuse('challenge_active');
      if (state.busy) return refuse('busy');

      /* Fresh evaluation of the last authoritative cart, not the cached
       * desired state: a shopper edit may have landed since the card rendered. */
      var cartLines = (state.lastCart && state.lastCart.items) || [];
      var desired = evaluate(cartLines, customerCtx(), state.manifest, Date.now());
      var entry = null;
      for (var i = 0; i < desired.deals.length; i++) {
        if (String(desired.deals[i].ruleId) === String(ruleId)) { entry = desired.deals[i]; break; }
      }
      if (!entry) return refuse('deal_not_available');
      if (entry.superseded) return refuse('deal_superseded');
      /* Held-but-unfulfillable (the sold-out second unit): the entry exists
       * only so the reconciler keeps the accepted line — never to add to. */
      if (entry.unavailable || !entry.option) return refuse('reward_out_of_stock');
      if (entry.remaining <= 0) return refuse('deal_taken');
      if (state.unavailableVariants[String(entry.option.variantId)]) return refuse('reward_out_of_stock');

      var props = {};
      props[DEAL_PROP] = rewardToken(entry.ruleId, entry.version);
      var variantId = entry.option.variantId;
      /* Units of this bundle held BEFORE the add — the yardstick that settles
       * an ambiguous rejection below. */
      var heldBefore = dealUnitsInCart(cartLines, entry.ruleId, variantId);
      state.busy = true;
      return cartApi.addSilently([{ id: variantId, quantity: 1, properties: props }], { source: SOURCE })
        .then(function (result) {
          state.busy = false;
          dispatchMutationResult(true, [String(variantId)], []);
          var cartState = result && cartApi.getCartState ? cartApi.getCartState(result) : null;
          if (cartState && Array.isArray(cartState.items)) {
            state.lastCart = cartState;
            state.passCount = 0;
            reconcile(cartState, { fromEngine: true });
          }
          return { ok: true, variantId: String(variantId), reason: null };
        }, function (err) {
          /* HC - an ambiguous accept is settled by the cart, not the error - 2026-09-01
           * A rejected addSilently is NOT proof the bundle stayed out: Shopify
           * may have accepted the POST and only the verification read failed.
           * Reporting "Nothing was added" there leaves a bundle sitting in the
           * cart that the card says is not there — and marking the variant
           * unavailable on top of it would make the next pass look sold out.
           * So read the authoritative cart (theme-queued, deduped) and believe
           * it: more units than we started with means the add landed. state.busy
           * stays true across that read, exactly as it was for the add. */
          var fail = function () {
            state.busy = false;
            if (!isTransientAddError(err)) state.unavailableVariants[String(variantId)] = true;
            var reason = String((err && err.message) || 'add_failed');
            dispatchMutationResult(false, [], [{ ruleId: entry.ruleId, variantId: String(variantId), reason: reason }]);
            return { ok: false, variantId: String(variantId), reason: reason };
          };
          var landed = function (cartState) {
            state.busy = false;
            dispatchMutationResult(true, [String(variantId)], []);
            state.lastCart = cartState;
            state.passCount = 0;
            reconcile(cartState, { fromEngine: true });
            return { ok: true, variantId: String(variantId), reason: null };
          };
          try {
            return Promise.resolve(refreshedCartResult(null)).then(function (refreshed) {
              var cartState = refreshed && refreshed.cartState;
              if (!cartState || !Array.isArray(cartState.items)) return fail();
              if (dealUnitsInCart(cartState.items, entry.ruleId, variantId) <= heldBefore) return fail();
              return landed(cartState);
            }, fail);
          } catch (refreshErr) {
            return fail();
          }
        });
    } catch (err) {
      state.busy = false;
      debugLog('acceptDeal failed open', err);
      return refuse('error');
    }
  }

  function dispatchDealRemovals(removed) {
    if (!removed || !removed.length) return;
    for (var i = 0; i < removed.length; i++) {
      try {
        document.dispatchEvent(new CustomEvent('ibgift:deal-removed', {
          detail: { ruleId: removed[i].ruleId, name: removed[i].name || '', reason: removed[i].reason, min: removed[i].min || 0 }
        }));
      } catch (err) { /* fail open */ }
    }
  }

  /* ------------------------------------------------------------------ *
   * Bootstrap
   * ------------------------------------------------------------------ */

  function init(config) {
    config = config || {};
    if (state.initialized) return api;
    state.initialized = true;

    var manifestSource = config.manifest ||
      (typeof window !== 'undefined' ? window.IBGiftManifest : null);

    /* A3 loader interface: window.IBGiftManifest is an {init, get} object, not
     * a promise. init(url) starts (or reuses) the single page fetch and returns
     * a promise that resolves the manifest or null — never rejects. */
    if (manifestSource && typeof manifestSource.then !== 'function' &&
        typeof manifestSource.init === 'function') {
      var manifestUrl = config.manifestUrl ||
        (typeof window !== 'undefined' ? window.__IB_GIFT_MANIFEST_URL__ : null);
      manifestSource = manifestSource.init(manifestUrl);
    }

    /* Review F3: subscribe BEFORE the manifest resolves. onCartUpdated
     * buffers the latest authoritative cart into state.lastCart even while
     * inactive (reconcile no-ops until activation), so qualifying activity
     * during manifest load is never lost. */
    try {
      document.addEventListener('cart:updated', onCartUpdated);
    } catch (err) {
      debugLog('cart listener attach failed; engine dormant', err && err.message);
      return api;
    }

    var activate = function (rawManifest) {
      var normalized = normalizeManifest(rawManifest);
      if (!normalized || normalized.rules.length === 0) {
        debugLog('no usable manifest; engine dormant');
        return;
      }
      state.manifest = normalized;
      state.active = true;
      api.active = true;
      /* Catch up: a cart that qualified before activation (the page-load cart,
       * or a mutation during manifest load) reconciles now. Review R2-2: the
       * embedded-state shortcut needed section HTML we don't have — use the
       * theme's queued authoritative read instead (deduped in-flight; one
       * init-time read, never on a shopper's add path). */
      if (state.lastCart) {
        state.passCount = 0;
        reconcile(state.lastCart, { fromEngine: false });
        return;
      }
      try {
        var cartApi = themeCart();
        if (cartApi && typeof cartApi.getCart === 'function') {
          cartApi.getCart().then(function (cart) {
            var cartState = cartApi.getCartState ? cartApi.getCartState(cart) : cart;
            if (!cartState || !Array.isArray(cartState.items)) return;
            /* A shopper event that raced the read wins. */
            if (state.lastCart) return;
            state.lastCart = cartState;
            state.passCount = 0;
            reconcile(cartState, { fromEngine: false });
          }, function () { /* fail open: the next shopper event syncs us */ });
        }
      } catch (err) { /* fail open */ }
    };

    try {
      if (manifestSource && typeof manifestSource.then === 'function') {
        manifestSource.then(activate, function (err) {
          debugLog('manifest load failed; engine dormant', err && err.message);
        });
      } else if (manifestSource) {
        activate(manifestSource);
      } else {
        debugLog('no manifest source; engine dormant');
      }
    } catch (err) {
      debugLog('init failed open', err);
    }
    return api;
  }

  var api = {
    version: ENGINE_VERSION,
    active: false,
    evaluate: evaluate,
    normalizeManifest: normalizeManifest,
    /* HC - nudge progress export (cart drawer v2, PR C) - 2026-08-20 */
    progress: progress,
    getManifest: function () { return state.manifest; },
    init: init,
    selectChoice: selectChoice,
    selectChoices: selectChoices,
    clearChoices: clearChoices,
    reconcile: function (cart) { return reconcile(cart, { fromEngine: false }); },
    /* HC - deal accept (2026-09-01): the only add path for a DEAL bundle. */
    acceptDeal: acceptDeal,
    _test: {
      state: state,
      ownedDealLines: ownedDealLines,
      computeDiff: computeDiff,
      detectOptOuts: detectOptOuts,
      parseRewardToken: parseRewardToken,
      decodeIdArray: decodeIdArray,
      legacyRewardLines: legacyRewardLines,
      seedLegacyChoicePicks: seedLegacyChoicePicks,
      adoptOwnedChoicePicks: adoptOwnedChoicePicks
    }
  };

  if (typeof window !== 'undefined') {
    window.IBGiftEngine = api;
    if (!window.IB_GIFT_ENGINE_MANUAL) {
      if (typeof document !== 'undefined' && document.readyState === 'loading' && document.addEventListener) {
        document.addEventListener('DOMContentLoaded', function () { init(); });
      } else {
        init();
      }
    }
  }
})();
